import {
  Alert,
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import { toast, ToastContainer } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { BUTTON_STYLES, TEXT_FIELD_STYLES } from "../../styles/componentStyles";
import {
  apiErrorMessage,
  createCampaignLink,
  getCampaignLink,
  getCampaignLinkFilters,
  updateCampaignLink,
} from "../../api/campaignLinks";
import {
  DESTINATIONS,
  EMPTY_VALUES,
  LIMITS,
  STANDARD_FIELDS,
  buildCampaignUrl,
  copyToClipboard,
  fromCampaign,
  newParamRow,
  referrerParams,
  toPayload,
  validateCampaign,
  valueWarning,
} from "./campaignLinkUrl";

const sectionTitle = { fontWeight: 600, color: "#17479e", mb: 0.5 };

function SectionCard({ title, description, action, children }) {
  return (
    <Card sx={{ boxShadow: 1, mb: 2 }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 1,
          }}
        >
          <Box>
            <Typography variant="subtitle1" sx={sectionTitle}>
              {title}
            </Typography>
            {description && (
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            )}
          </Box>
          {action}
        </Box>
        {children}
      </CardContent>
    </Card>
  );
}

function CampaignLinkForm() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const copyFrom = searchParams.get("copyFrom");
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [initialValues, setInitialValues] = useState(EMPTY_VALUES);
  const [loaded, setLoaded] = useState(null); // campaign being edited
  const [isLoading, setIsLoading] = useState(isEdit || Boolean(copyFrom));
  const [loadError, setLoadError] = useState(null);
  const [suggestions, setSuggestions] = useState({ sources: [], mediums: [] });

  useEffect(() => {
    getCampaignLinkFilters()
      .then((data) => setSuggestions({ sources: data?.sources || [], mediums: data?.mediums || [] }))
      .catch(() => {}); // suggestions are optional
  }, []);

  useEffect(() => {
    const sourceId = id || copyFrom;
    if (!sourceId) return;
    setIsLoading(true);
    setLoadError(null);
    getCampaignLink(sourceId)
      .then((campaign) => {
        if (isEdit && campaign.status === "DELETED") {
          setLoadError("This campaign is deleted. Restore it from the campaign list before editing.");
          return;
        }
        setInitialValues(fromCampaign(campaign, { copy: !isEdit }));
        setLoaded(isEdit ? campaign : null);
      })
      .catch((err) => setLoadError(apiErrorMessage(err, "Failed to load the campaign. Please try again.")))
      .finally(() => setIsLoading(false));
  }, [id, copyFrom, isEdit]);

  const save = async (values, { setSubmitting }) => {
    try {
      const saved = isEdit
        ? await updateCampaignLink(id, toPayload(values))
        : await createCampaignLink(toPayload(values));
      navigate("/campaign-links", {
        state: {
          message: isEdit ? "Campaign updated successfully!" : "Campaign saved successfully!",
          savedId: saved.id,
        },
      });
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to save the campaign. Please try again."), { autoClose: 4000 });
      setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validate: validateCampaign,
    onSubmit: save,
  });
  const { values, errors, touched } = formik;

  const generatedUrl = useMemo(() => buildCampaignUrl(values), [values]);
  const pairs = useMemo(() => referrerParams(values), [values]);
  const hasErrors = Object.keys(errors).length > 0;
  const requiredMissing = STANDARD_FIELDS.some((f) => f.required && !String(values[f.key] || "").trim());
  const linkReady = !hasErrors && !requiredMissing;
  const linkChanged = isEdit && loaded && generatedUrl !== loaded.generatedUrl;

  const handleSaveClick = async () => {
    const validation = await formik.validateForm();
    if (Object.keys(validation).length) {
      toast.error("Please fix the highlighted fields.", { autoClose: 3000 });
    }
    formik.submitForm(); // also marks every field touched so any errors show
  };

  const handleCopy = async () => {
    try {
      await copyToClipboard(generatedUrl);
      toast.success("Link copied to clipboard!", { autoClose: 2000 });
    } catch {
      toast.error("Could not copy the link. Select it and copy manually.");
    }
  };

  const handleReset = () => formik.resetForm({ values: initialValues });

  const setParams = (rows) => formik.setFieldValue("customParams", rows);
  const addParam = () => setParams([...values.customParams, newParamRow()]);
  const removeParam = (index) => {
    setParams(values.customParams.filter((_, i) => i !== index));
    // Keep touched flags aligned with the remaining rows.
    const touchedRows = Array.isArray(touched.customParams) ? touched.customParams : [];
    formik.setFieldTouched("customParams", touchedRows.filter((_, i) => i !== index), false);
  };

  const rowError = (index, field) =>
    touched.customParams?.[index]?.[field] && errors.customParams?.[index]?.[field];

  const title = isEdit ? "Edit Campaign" : copyFrom ? "Duplicate Campaign" : "New Campaign";
  const destination = DESTINATIONS[values.destination] || DESTINATIONS.ANDROID_APP;

  const renderStandardField = (f) => {
    const fieldError = touched[f.key] && errors[f.key];
    const warning = !fieldError && valueWarning(values[f.key]);
    const helper = fieldError || warning || f.help;
    const textFieldProps = {
      label: f.label,
      name: f.key,
      required: f.required,
      placeholder: f.placeholder,
      error: Boolean(fieldError),
      helperText: (
        <Box component="span" sx={{ color: warning ? "warning.dark" : undefined }}>
          {helper}
        </Box>
      ),
      inputProps: { maxLength: LIMITS.value + 20, "aria-label": f.label },
      ...TEXT_FIELD_STYLES.standard,
      margin: "none",
    };
    const options = f.key === "utmSource" ? suggestions.sources : f.key === "utmMedium" ? suggestions.mediums : null;
    if (options) {
      return (
        <Autocomplete
          freeSolo
          options={options}
          value={values[f.key]}
          inputValue={values[f.key]}
          onInputChange={(_, v) => formik.setFieldValue(f.key, v)}
          onBlur={() => formik.setFieldTouched(f.key, true)}
          renderInput={(params) => (
            <TextField {...params} {...textFieldProps} inputProps={{ ...params.inputProps, ...textFieldProps.inputProps }} />
          )}
        />
      );
    }
    return (
      <TextField
        {...textFieldProps}
        value={values[f.key]}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
      />
    );
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <ToastContainer />
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2, mt: 1 }}>
        <Link underline="hover" color="inherit" to="/campaign-links">
          Campaign Links
        </Link>
        <Typography sx={{ color: "text.primary", fontWeight: 600 }}>{title}</Typography>
      </Breadcrumbs>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && !loadError && (
        <Grid container spacing={2} component="form" noValidate onSubmit={(e) => e.preventDefault()}>
          <Grid item xs={12} lg={7}>
            <SectionCard title="1. Destination" description="Where students land when they open the link.">
              <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                <Chip label={destination.label} color="primary" variant="outlined" />
                <Typography variant="body2" color="text.secondary">
                  Package <strong>{destination.packageId}</strong>
                </Typography>
              </Box>
            </SectionCard>

            <SectionCard
              title="2. Standard campaign parameters (UTM)"
              description="Source, Medium and Campaign Name are required. Use lowercase words joined by _ so reports stay consistent."
            >
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                {STANDARD_FIELDS.map((f) => (
                  <Grid item xs={12} md={6} key={f.key}>
                    {renderStandardField(f)}
                  </Grid>
                ))}
              </Grid>
            </SectionCard>

            <SectionCard
              title="3. Custom tracking parameters"
              description="Optional extra details, e.g. institute_name = ABC_College or college_code = COL001."
              action={
                <Button
                  startIcon={<AddIcon />}
                  onClick={addParam}
                  disabled={values.customParams.length >= LIMITS.customParams}
                  {...BUTTON_STYLES.secondary}
                >
                  Add Parameter
                </Button>
              }
            >
              {values.customParams.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: "center", py: 2 }}>
                  No custom parameters. Click "Add Parameter" to add one.
                </Typography>
              ) : (
                <Box sx={{ mt: 1 }}>
                  {values.customParams.map((row, index) => (
                    <Box
                      key={row.key}
                      sx={{ display: "flex", gap: 1, alignItems: "flex-start", mt: 1.5 }}
                      data-testid="custom-param-row"
                    >
                      <TextField
                        label="Parameter name"
                        name={`customParams.${index}.name`}
                        value={row.name}
                        placeholder="e.g. institute_name"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={Boolean(rowError(index, "name"))}
                        helperText={rowError(index, "name") || " "}
                        inputProps={{ maxLength: LIMITS.paramName + 10 }}
                        {...TEXT_FIELD_STYLES.standard}
                        margin="none"
                        size="small"
                      />
                      <Typography sx={{ pt: 1, color: "text.secondary" }}>=</Typography>
                      <TextField
                        label="Value"
                        name={`customParams.${index}.value`}
                        value={row.value}
                        placeholder="e.g. ABC_College"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={Boolean(rowError(index, "value"))}
                        helperText={rowError(index, "value") || " "}
                        inputProps={{ maxLength: LIMITS.value + 20 }}
                        {...TEXT_FIELD_STYLES.standard}
                        margin="none"
                        size="small"
                      />
                      <Tooltip title="Remove parameter" arrow placement="top">
                        <IconButton
                          color="error"
                          size="small"
                          aria-label={`Remove parameter ${row.name || index + 1}`}
                          onClick={() => removeParam(index)}
                          sx={{ mt: 0.5, "&:hover": { backgroundColor: "error.light", color: "white" } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                  {errors.customParamsCount && (
                    <Typography variant="caption" color="error">
                      {errors.customParamsCount}
                    </Typography>
                  )}
                </Box>
              )}
            </SectionCard>

            <SectionCard title="Notes" description="Optional. Only visible in the portal, not part of the link.">
              <TextField
                name="notes"
                label="Notes"
                value={values.notes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                multiline
                minRows={2}
                placeholder="e.g. Shared with ABC College faculty WhatsApp group"
                error={Boolean(touched.notes && errors.notes)}
                helperText={(touched.notes && errors.notes) || `${String(values.notes || "").length}/${LIMITS.notes}`}
                {...TEXT_FIELD_STYLES.standard}
              />
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Card sx={{ boxShadow: 2, position: { lg: "sticky" }, top: { lg: 16 } }}>
              <CardContent>
                <Typography variant="subtitle1" sx={sectionTitle}>
                  Generated link
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Updates automatically as you type.
                </Typography>
                <Box
                  data-testid="generated-url"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: linkReady ? "primary.main" : "divider",
                    backgroundColor: linkReady ? "#17469e0d" : "#f5f5f5",
                    fontFamily: "monospace",
                    fontSize: "0.8rem",
                    wordBreak: "break-all",
                    color: linkReady ? "text.primary" : "text.secondary",
                  }}
                >
                  {generatedUrl}
                </Box>
                {!linkReady && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                    {requiredMissing
                      ? "Fill in Campaign Source, Medium and Name to complete the link."
                      : errors.url || "Fix the highlighted fields to complete the link."}
                  </Typography>
                )}
                {linkChanged && linkReady && (
                  <Alert severity="warning" sx={{ mt: 1.5 }}>
                    Saving replaces this campaign's link. Copies of the old link that were already shared keep
                    working, but won't match this campaign any more.
                  </Alert>
                )}

                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Parameters in this link ({pairs.length})
                </Typography>
                {pairs.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    None yet.
                  </Typography>
                ) : (
                  <Box component="dl" sx={{ m: 0, display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 2, rowGap: 0.5 }}>
                    {pairs.map(([name, value]) => (
                      <React.Fragment key={name}>
                        <Typography component="dt" variant="body2" sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                          {name}
                        </Typography>
                        <Typography component="dd" variant="body2" sx={{ m: 0, wordBreak: "break-word" }}>
                          {value}
                        </Typography>
                      </React.Fragment>
                    ))}
                  </Box>
                )}

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 3, justifyContent: "flex-end" }}>
                  <Button startIcon={<RestartAltIcon />} onClick={handleReset} {...BUTTON_STYLES.secondary}>
                    Reset Form
                  </Button>
                  <Button
                    startIcon={<ContentCopyIcon />}
                    onClick={handleCopy}
                    disabled={!linkReady}
                    {...BUTTON_STYLES.secondary}
                  >
                    Copy Link
                  </Button>
                  <Button onClick={handleSaveClick} disabled={formik.isSubmitting} {...BUTTON_STYLES.primary}>
                    {formik.isSubmitting ? "Saving..." : isEdit ? "Save Changes" : "Save Campaign"}
                  </Button>
                </Box>
                <Box sx={{ textAlign: "right", mt: 1 }}>
                  <Button component={Link} to="/campaign-links" size="small" sx={{ textTransform: "none" }}>
                    Cancel and go back
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

    </Box>
  );
}

export default CampaignLinkForm;
